// Deck viewer, odds panel, settings, debug overlay.
import { useEffect, useMemo, useState } from 'react';
import type { KeyAction } from '../../sim/types';
import { defaultKeys } from '../../sim/meta';
import { keyName, setKeyCapture } from '../keyboard';
import { CodexPanel } from './Codex';
import { B } from '../../content/balance';
import { SYMBOLS } from '../../content/registry';
import { formatPercent } from '../../i18n/format';
import { t } from '../../i18n/t';
import { audio } from '../../audio/audio';
import type { OddsResult } from '../../sim/odds';
import { patternMult } from '../../sim/patterns';
import type { PatternId, Settings } from '../../sim/types';
import { deckSummary, modsOf } from '../../state/selectors';
import { handInterval } from '../../sim/hand';
import { exportString, importString, listBackups, deserialize, toPlain, clearLocal } from '../../state/save';
import { dispatch, setUI, store, toast, useGame, useUI } from '../../state/store';
import { Btn, Frame, Icon, KText, useFmt } from '../common';
import { tip } from '../tooltip/Tooltip';
import { SymbolTip } from '../intermission/Intermission';

function PanelShell({ title, children, width = 380 }: { title: string; children: React.ReactNode; width?: number }) {
  return (
    <div className="overlay interactive" onClick={() => setUI({ panel: null })}>
      <Frame title={title} style={{ width, maxHeight: 316 }}>
        <div onClick={(e) => e.stopPropagation()} className="scroll" style={{ maxHeight: 272 }}>
          {children}
        </div>
        <div style={{ position: 'absolute', right: 4, top: 3 }}>
          <Btn kind="ghost" onClick={() => setUI({ panel: null })}>
            ✕
          </Btn>
        </div>
      </Frame>
    </div>
  );
}

function Deck() {
  const game = useGame((g) => g);
  const [sort, setSort] = useState<'count' | 'value' | 'rarity'>('count');
  const entries = deckSummary(game);
  const rank = { C: 0, U: 1, R: 2, L: 3 } as Record<string, number>;
  const sorted = [...entries].sort((a, b) =>
    sort === 'count' ? b.count - a.count : sort === 'value' ? (SYMBOLS.get(b.defId)?.baseValue ?? 0) - (SYMBOLS.get(a.defId)?.baseValue ?? 0) : (rank[b.rarity] ?? 0) - (rank[a.rarity] ?? 0),
  );
  return (
    <PanelShell title={`${t('ui.deck.title')} (${game.run?.deck.length ?? 0})`}>
      <div className="row" style={{ justifyContent: 'flex-start', gap: 4, marginBottom: 6 }}>
        {(['count', 'value', 'rarity'] as const).map((s) => (
          <Btn key={s} kind={sort === s ? 'primary' : 'ghost'} onClick={() => setSort(s)}>
            {s === 'count' ? '×' : t(`ui.deck.sort.${s}`)}
          </Btn>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
        {sorted.map((e) => (
          <div key={e.defId} className="card" style={{ textAlign: 'center' }} {...tip(<SymbolTip id={e.defId} />)}>
            <Icon id={`sym_${e.defId}`} />
            <div>{t(`sym.${e.defId}.name`)}</div>
            <div className="h">{t('ui.deck.copies', { n: e.count })}</div>
            <div className="dim">{t('ui.deck.chance', { p: formatPercent(e.chance) })}</div>
            {e.maxBonus > 0 && <div className="kw-value">+{e.maxBonus}</div>}
            {e.rotsIn !== null && <div className="kw-hunger">{t('ui.deck.rotsIn', { n: e.rotsIn })}</div>}
            {SYMBOLS.get(e.defId)?.tags.includes('rotten') && <div className="kw-hunger">{t('ui.deck.rotten')}</div>}
          </div>
        ))}
      </div>
    </PanelShell>
  );
}

let worker: Worker | null = null;
let reqId = 0;

function Odds() {
  const game = useGame((g) => g);
  const fmt = useFmt();
  const [res, setRes] = useState<OddsResult | null>(null);
  const mods = modsOf(game);
  useEffect(() => {
    if (!game.run) return;
    worker ??= new Worker(new URL('../../workers/odds.worker.ts', import.meta.url), { type: 'module' });
    const id = ++reqId;
    setRes(null);
    const onMsg = (e: MessageEvent<{ id: number; result: OddsResult }>) => {
      if (e.data.id === id) setRes(e.data.result);
    };
    worker.addEventListener('message', onMsg);
    worker.postMessage({ id, state: toPlain(game) });
    return () => worker?.removeEventListener('message', onMsg);
  }, [game]);
  const run = game.run;
  const size = run?.size ?? 'bandit';
  const patterns = useMemo(
    () => [...mods.patterns].filter((p) => (p === 'gorge' ? size !== 'bandit' : p === 'bandit' ? size === 'bandit' : true)),
    [mods, size],
  );
  if (!run) return null;
  if (mods.blind)
    return (
      <PanelShell title={t('ui.odds.title')} width={260}>
        <div className="kw-bad">{t('ui.odds.blind')}</div>
      </PanelShell>
    );
  return (
    <PanelShell title={t('ui.odds.title')} width={400}>
      <div className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
        <div className="col" style={{ flex: 1 }}>
          <div className="h" style={{ color: '#fbb954' }}>
            {t('ui.odds.symbols')}
          </div>
          {deckSummary(game).map((e) => (
            <div key={e.defId} className="row">
              <span>
                <Icon id={`sym_${e.defId}`} scale={0.375} /> {t(`sym.${e.defId}.name`)} ×{e.count}
              </span>
              <span>{formatPercent(e.chance)}</span>
            </div>
          ))}
        </div>
        <div className="col" style={{ flex: 1 }}>
          <div className="h" style={{ color: '#fbb954' }}>
            {t('ui.odds.patterns')}
          </div>
          {patterns.map((p) => (
            <div key={p} className="row">
              <span>{t(`pattern.${p}`)}</span>
              <span>
                <span className="kw-mult">×{patternMult(p as PatternId, run.size, mods, run.hymns)}</span>{' '}
                <span className="dim">{res ? formatPercent(res.patternRates[p as PatternId] ?? 0) : '…'}</span>
              </span>
            </div>
          ))}
          <div className="sep" />
          <div className="row">
            <span>{t('ui.odds.ev')}</span>
            <span className="kw-value">{res ? fmt(res.ev) : t('ui.odds.running')}</span>
          </div>
          <div className="row">
            <span>{t('ui.odds.meet')}</span>
            <span className="h">{res?.meetChance != null ? formatPercent(res.meetChance) : '…'}</span>
          </div>
          <div className="sep" />
          <KText text={t('ui.odds.luck', { n: mods.luck })} />
          <div style={{ height: 4 }} />
          <KText text={t('ui.odds.pity', { n: mods.pityAfter, luck: B.PITY_BASE_LUCK, step: B.PITY_STEP })} />
        </div>
      </div>
    </PanelShell>
  );
}

const KEY_ROWS: KeyAction[][] = [
  ['pull', 'pay', 'hand', 'collect', 'gamble'],
  ['hold1', 'hold2', 'hold3', 'hold4', 'hold5'],
  ['up1', 'up2', 'up3', 'up4', 'up5'],
  ['down1', 'down2', 'down3', 'down4', 'down5'],
];
const keyLabel = (k: string) => (k === ' ' ? t('settings.key.space') : k.length === 1 ? k.toUpperCase() : k.toUpperCase());

/** Rebind a gameplay key: click, then press the new key (Esc cancels). */
function KeyBindings() {
  const keys = useGame((g) => g.meta.settings.keys);
  const [listening, setListening] = useState<KeyAction | null>(null);
  useEffect(() => {
    if (!listening) return;
    setKeyCapture(true);
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key !== 'Escape' && e.key !== 'Tab' && e.key !== 'F1') dispatch({ t: 'SET_SETTINGS', settings: { keys: { ...keys, [listening]: keyName(e) } } });
      setListening(null);
    };
    window.addEventListener('keydown', onKey, { capture: true, once: true });
    return () => {
      window.removeEventListener('keydown', onKey, { capture: true });
      setKeyCapture(false);
    };
  }, [listening, keys]);
  return (
    <div className="col" style={{ gap: 2 }}>
      <div className="row">
        <span>{t('settings.keys')}</span>
        <Btn kind="ghost" onClick={() => dispatch({ t: 'SET_SETTINGS', settings: { keys: defaultKeys() } })}>
          {t('settings.keys.reset')}
        </Btn>
      </div>
      {KEY_ROWS.map((row, i) => (
        <div key={i} className="row" style={{ justifyContent: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          {row.map((a) => (
            <Btn key={a} kind={listening === a ? 'primary' : 'ghost'} onClick={() => setListening(a)} title={t(`settings.key.${a}`)} testId={`key-${a}`}>
              {`${t(`settings.key.${a}`)} · ${listening === a ? '…' : keyLabel(keys[a])}`}
            </Btn>
          ))}
        </div>
      ))}
    </div>
  );
}

function SettingsPanel() {
  const s = useGame((g) => g.meta.settings);
  const [imp, setImp] = useState('');
  const [resetStep, setResetStep] = useState(0);
  const [backups, setBackups] = useState<{ savedAt: number; text: string }[]>([]);
  useEffect(() => {
    void listBackups().then(setBackups);
  }, []);
  const set = (p: Partial<Settings>) => {
    dispatch({ t: 'SET_SETTINGS', settings: p });
    const next = { ...s, ...p };
    audio.setVolumes({ master: next.master, music: next.music, sfx: next.sfx });
  };
  const slider = (key: 'master' | 'music' | 'sfx' | 'shake') => (
    <div className="row">
      <span>{t(`settings.${key}`)}</span>
      <input type="range" min={0} max={1} step={0.05} value={s[key]} onChange={(e) => set({ [key]: Number(e.target.value) })} />
    </div>
  );
  const toggle = (key: 'reduceFlashing' | 'crt' | 'muscleMemory' | 'tips') => (
    <div className="row">
      <span>{t(`settings.${key}`)}</span>
      <Btn kind={s[key] ? 'primary' : 'ghost'} onClick={() => set({ [key]: !s[key] })}>
        {s[key] ? t('settings.on') : t('settings.off')}
      </Btn>
    </div>
  );
  return (
    <PanelShell title={t('ui.settings')} width={330}>
      <div className="col" style={{ gap: 5 }}>
        {slider('master')}
        {slider('music')}
        {slider('sfx')}
        <div className="row">
          <span>{t('settings.speed')}</span>
          <span className="row" style={{ gap: 2 }}>
            {[1, 1.5, 2, 3].map((v) => (
              <Btn key={v} kind={s.speed === v ? 'primary' : 'ghost'} onClick={() => set({ speed: v })}>
                {v}×
              </Btn>
            ))}
          </span>
        </div>
        {slider('shake')}
        {toggle('reduceFlashing')}
        {toggle('crt')}
        {toggle('muscleMemory')}
        {toggle('tips')}
        <div className="row">
          <span>{t('settings.repeat')}</span>
          <span className="row" style={{ gap: 2 }}>
            {[400, 700, 1000].map((v) => (
              <Btn key={v} kind={s.repeatMs === v ? 'primary' : 'ghost'} onClick={() => set({ repeatMs: v })}>
                {t(`settings.repeat.${v}`)}
              </Btn>
            ))}
          </span>
        </div>
        <KeyBindings />
        <div className="row">
          <span>{t('settings.notation')}</span>
          <span className="row" style={{ gap: 2 }}>
            {(['suffix', 'scientific', 'engineering'] as const).map((v) => (
              <Btn key={v} kind={s.notation === v ? 'primary' : 'ghost'} onClick={() => set({ notation: v })}>
                {t(`settings.notation.${v}`)}
              </Btn>
            ))}
          </span>
        </div>
        <div className="row">
          <span>{t('settings.decay')}</span>
          <input type="range" min={0} max={1} step={0.05} defaultValue={0} onChange={(e) => audio.music.setDecay(Number(e.target.value))} />
        </div>
        <div className="sep" />
        <div className="row" style={{ justifyContent: 'flex-start', gap: 4 }}>
          <Btn
            onClick={() => {
              const str = exportString(store.getState().game, Date.now());
              void navigator.clipboard?.writeText(str);
              setImp(str);
              toast(t('ui.save.copied'));
            }}
          >
            {t('ui.save.export')}
          </Btn>
          <Btn
            onClick={() => {
              try {
                const g = importString(imp);
                store.getState().replaceGame(g);
                setUI({ screen: 'title', panel: null });
              } catch {
                toast(t('ui.save.bad'));
              }
            }}
            disabled={!imp}
          >
            {t('ui.save.import')}
          </Btn>
        </div>
        <textarea rows={3} placeholder={t('ui.save.importPrompt')} value={imp} onChange={(e) => setImp(e.target.value)} />
        {backups.length > 0 && (
          <div className="col">
            <span className="dim">{t('ui.save.backups')}</span>
            {backups.map((b) => (
              <div className="row" key={b.savedAt}>
                <span>{new Date(b.savedAt).toLocaleString()}</span>
                <Btn
                  kind="ghost"
                  onClick={() => {
                    store.getState().replaceGame(deserialize(b.text));
                    setUI({ screen: 'title', panel: null });
                  }}
                >
                  {t('ui.save.restore')}
                </Btn>
              </div>
            ))}
          </div>
        )}
        <Btn
          kind="danger"
          onClick={() => {
            if (resetStep < 2) return setResetStep(resetStep + 1);
            clearLocal();
            location.reload();
          }}
        >
          {resetStep === 0 ? t('ui.save.reset') : resetStep === 1 ? t('ui.save.resetConfirm') : t('ui.save.resetConfirm2')}
        </Btn>
      </div>
    </PanelShell>
  );
}

const PRIORITY_TAGS = ['fruit', 'classic', 'bar', 'vermin', 'light', 'cursed', 'sigil', 'wild'];

/** The Hand's settings (docs/04 §21): pay-early threshold, feeding threshold, stops, draft priority. */
function HandPanel() {
  const game = useGame((g) => g);
  const hand = game.meta.hand;
  const m = modsOf(game);
  const set = (p: Partial<typeof hand>) => dispatch({ t: 'SET_HAND', hand: p });
  const toggle = (key: 'stopOnBig' | 'stopOnBonus' | 'declineGambles', label: string) => (
    <div className="row">
      <span>{label}</span>
      <Btn kind={hand[key] ? 'primary' : 'ghost'} onClick={() => set({ [key]: !hand[key] })}>
        {hand[key] ? t('settings.on') : t('settings.off')}
      </Btn>
    </div>
  );
  return (
    <PanelShell title={t('ui.hand.title')} width={320}>
      <div className="col" style={{ gap: 5 }}>
        <div className="dim">{t('ui.hand.speed', { s: handInterval(m) })}</div>
        {m.hand.autopay ? (
          <div className="row">
            <span>{t('ui.hand.payAt', { x: hand.payAt.toFixed(1) })}</span>
            <input type="range" min={1} max={3} step={0.1} value={hand.payAt} onChange={(e) => set({ payAt: Number(e.target.value) })} />
          </div>
        ) : null}
        {m.hand.autofeed ? (
          <div className="row">
            <span>{t('ui.hand.feedAt', { n: hand.feedAt })}</span>
            <input type="range" min={20} max={100} step={5} value={hand.feedAt} onChange={(e) => set({ feedAt: Number(e.target.value) })} />
          </div>
        ) : null}
        {toggle('stopOnBig', t('ui.hand.stopBig'))}
        {toggle('stopOnBonus', t('ui.hand.stopBonus'))}
        {m.bonuses.has('hilo') ? toggle('declineGambles', t('ui.hand.declineGambles')) : null}
        <div className="row">
          <span>{t('ui.hand.stopLow', { n: hand.stopLowSpins })}</span>
          <input type="range" min={0} max={5} step={1} value={hand.stopLowSpins} onChange={(e) => set({ stopLowSpins: Number(e.target.value) })} />
        </div>
        {m.hand.autodraft ? (
          <div className="col">
            <span className="h" style={{ color: '#fbb954' }}>
              {t('ui.hand.priority')}
            </span>
            <span className="dim">{t('ui.hand.priorityHint')}</span>
            <div className="row" style={{ flexWrap: 'wrap', justifyContent: 'flex-start', gap: 3 }}>
              {PRIORITY_TAGS.map((tag) => (
                <Btn key={tag} kind="ghost" onClick={() => !hand.draftPriority.includes(`tag:${tag}`) && set({ draftPriority: [...hand.draftPriority, `tag:${tag}`] })}>
                  {t(`tag.${tag}`)}
                </Btn>
              ))}
            </div>
            <div className="row" style={{ flexWrap: 'wrap', justifyContent: 'flex-start', gap: 3 }}>
              {hand.draftPriority.map((p, i) => (
                <span key={p} className="kw-value" onContextMenu={(e) => { e.preventDefault(); set({ draftPriority: hand.draftPriority.filter((x) => x !== p) }); }} onClick={() => set({ draftPriority: hand.draftPriority.filter((x) => x !== p) })} style={{ cursor: 'pointer' }}>
                  {i + 1}. {p.startsWith('tag:') ? t(`tag.${p.slice(4)}`) : t(`sym.${p}.name`)}
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </PanelShell>
  );
}

export function Panels() {
  const panel = useUI((u) => u.panel);
  if (panel === 'deck') return <Deck />;
  if (panel === 'odds') return <Odds />;
  if (panel === 'settings') return <SettingsPanel />;
  if (panel === 'hand') return <HandPanel />;
  if (panel === 'ledger') return <LedgerPanel />;
  if (panel === 'codex') return <CodexPanel />;
  return null;
}

/** Dolores's pages (docs/06 §5), in her handwriting. */
function LedgerPanel() {
  const pages = useGame((g) => g.meta.lifetime.ledgerPages);
  const sorted = [...pages].sort();
  return (
    <PanelShell title={t('ui.ledger.title')} width={360}>
      {sorted.length === 0 && <div className="dim">{t('ui.ledger.empty')}</div>}
      <div className="col" style={{ gap: 6 }}>
        {sorted.map((id) => (
          <div key={id} className="paper" style={{ padding: '4px 6px' }}>
            <div className="dim" style={{ color: '#694f62' }}>
              {t('ui.ledger.page', { n: Number(id.slice(-2)) })}
            </div>
            <div className="handwriting">{t(`ledger.${id}`)}</div>
          </div>
        ))}
      </div>
    </PanelShell>
  );
}

export function Debug() {
  const on = useUI((u) => u.debug);
  const game = useGame((g) => g);
  const events = useUI((u) => u.eventLog);
  if (!on) return null;
  const run = game.run;
  const brief = {
    phase: run?.phase,
    window: run && { n: run.window.n, tribute: run.window.tribute.toString(), spinsLeft: run.window.spinsLeft },
    coins: run?.coins.toString(),
    souls: game.meta.souls,
    nodes: game.meta.nodes,
    deck: run?.deck.map((d) => d.defId).join(','),
    stops: run?.stops,
    charges: run?.charges,
    charms: run?.charms,
  };
  return (
    <div className="interactive" style={{ position: 'absolute', inset: '16px 130px 12px 130px', background: '#2e222f', opacity: 0.94, padding: 4, zIndex: 40 }}>
      <div className="row">
        <span className="h">{t('ui.debug.title')}</span>
        <span className="row" style={{ gap: 3 }}>
          <Btn kind="ghost" onClick={() => dispatch({ t: 'DEBUG', action: 'souls', amount: 100 })}>
            +100◈
          </Btn>
          <Btn kind="ghost" onClick={() => dispatch({ t: 'DEBUG', action: 'coins', amount: 1000 })}>
            +1K¢
          </Btn>
          <Btn kind="ghost" onClick={() => dispatch({ t: 'DEBUG', action: 'spins', amount: 5 })}>
            +5⟳
          </Btn>
        </span>
      </div>
      <div className="row scroll" style={{ alignItems: 'flex-start', height: 300, gap: 6 }}>
        <pre style={{ margin: 0, flex: 1, whiteSpace: 'pre-wrap', font: 'inherit' }}>{JSON.stringify(brief, null, 1)}</pre>
        <pre style={{ margin: 0, flex: 1, whiteSpace: 'pre-wrap', font: 'inherit', color: '#9babb2' }}>
          {events
            .slice(-60)
            .map((e) => e.e + (e.e === 'SAY' ? ` ${e.key}` : e.e === 'REEL_STOP' ? ` r${e.reel}` : ''))
            .join('\n')}
        </pre>
      </div>
    </div>
  );
}
